import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { networkApi, type RefererRow } from '../../api/network.js';
import { EmptyBox, ErrorBox, Field, Pagination, SearchInput, Spinner } from '../../components/ui.js';
import { datetime, text, truncate } from '../../lib/format.js';
import { useUrlSearchFields, useUrlUpdate } from '../../lib/hooks.js';
import {
  AgentLabel,
  DetailModal,
  HostLink,
  MonthSelect,
  PER_PAGE,
  SiteBadge,
  descendingNumber,
  monthLabel,
} from './shared.js';

/**
 * 리퍼러 목록 — 레거시 /xadm/report_network_referer.php.
 *
 * 광고를 타고 들어온 유입 로그 한 줄씩이다(ALK_REFERER_network). 원천 적재가
 * 2024-10 에 멈춰서 과거 조회 전용이다 - 기본값(이번 달)으로 열면 빈 표가
 * 나오는 게 정상이고, 달을 골라야 자료가 보인다.
 *
 * 원본과 달라진 점:
 *  - 검색 버튼 없이 입력하면 바로 조회한다(다른 목록 화면과 같은 방식). 조건은
 *    URL 에 남아서 뒤로가기·링크 공유가 된다.
 *  - 월 선택 상자에 자료가 있는 달만 나온다(원본은 2022년까지 12달씩 전부 찍었다).
 *  - 긴 값(유입 URL·파라미터)은 표에서 줄여 보여주고 '상세'에서 전체를 본다
 *    (원본은 칸마다 textarea 였다).
 */

export function RefererListPage() {
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const [filters, setFilter] = useUrlSearchFields(params, update, ['partner', 'refhost']);

  const searchYm = params.get('searchYm') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const page = Number(params.get('page') || '1');
  const [detail, setDetail] = useState<RefererRow | null>(null);

  const months = useQuery({
    queryKey: ['network', 'referers', 'months'],
    queryFn: networkApi.refererMonths,
    staleTime: 10 * 60_000,
  });

  const list = useQuery({
    queryKey: ['network', 'referers', { searchYm, from, to, ...filters, page }],
    queryFn: () =>
      networkApi.referers({
        searchYm: searchYm || undefined,
        from: from || undefined,
        to: to || undefined,
        partner: filters.partner || undefined,
        refhost: filters.refhost || undefined,
        page,
        perPage: PER_PAGE,
      }),
    placeholderData: keepPreviousData,
  });

  const data = list.data;

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            리퍼러 목록
            {list.isFetching && <span className="page-head__busy">갱신 중…</span>}
          </h1>
          <p className="page-desc">
            광고를 타고 들어온 유입 기록입니다. 달과 기간으로 먼저 좁혀야 조회됩니다 — 원천 적재는 2024-10 에
            멈춰 있어 그 이후 달을 고르면 빈 표가 나옵니다.
          </p>
        </div>
      </header>

      <div className="filters">
        <Field label="조회 월">
          <MonthSelect
            months={months.data?.months ?? []}
            value={searchYm || data?.searchYm || ''}
            onChange={(v) => update({ searchYm: v })}
          />
        </Field>
        <Field label="유입일 (시작)">
          <input
            className="input"
            type="date"
            aria-label="시작일"
            value={from || data?.from || ''}
            onChange={(e) => update({ from: e.target.value })}
          />
        </Field>
        <Field label="유입일 (종료)">
          <input
            className="input"
            type="date"
            aria-label="종료일"
            value={to || data?.to || ''}
            onChange={(e) => update({ to: e.target.value })}
          />
        </Field>
        <Field label="파트너 아이디 (정확히 일치)">
          <SearchInput
            value={filters.partner ?? ''}
            placeholder="bnn_pop_onclicka"
            onChange={(v) => setFilter('partner', v)}
          />
        </Field>
        <Field label="리퍼러 검색 (도메인)">
          <SearchInput
            value={filters.refhost ?? ''}
            placeholder="hitomi.la"
            onChange={(v) => setFilter('refhost', v)}
          />
        </Field>
        <button
          type="button"
          className="btn btn--ghost filters__reset"
          onClick={() => setParams(new URLSearchParams(), { replace: true })}
        >
          조건 초기화
        </button>
      </div>

      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : data && data.items.length === 0 ? (
        <EmptyBox>조건에 맞는 유입 기록이 없습니다.</EmptyBox>
      ) : (
        data && (
          <>
            <div className="table-wrap">
              <table className="table network-table">
                <thead>
                  <tr>
                    <th className="num">번호</th>
                    <th>년월</th>
                    <th>파트너ID</th>
                    <th>리퍼러 도메인</th>
                    <th>리퍼러 상세URL</th>
                    <th>유입파라미터</th>
                    <th>유입IP</th>
                    <th>접속기기</th>
                    <th>접속시간</th>
                    <th>상세</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row, i) => (
                    <tr key={row.uid}>
                      <td className="num td-muted">{descendingNumber(data.total, data.page, data.perPage, i)}</td>
                      <td>{monthLabel(row.yearmonth)}</td>
                      <td>
                        <code className="code">{text(row.partner)}</code>
                      </td>
                      <td className="network-host">
                        <HostLink host={row.refhost} />
                        <SiteBadge site={row.site} />
                      </td>
                      <td className="td-clip" title={row.referer ?? undefined}>
                        {truncate(row.referer, 50)}
                      </td>
                      <td className="td-clip" title={row.param ?? undefined}>
                        {truncate(row.param, 40)}
                      </td>
                      <td>{text(row.ip)}</td>
                      <td>
                        <AgentLabel agent={row.agent} />
                      </td>
                      <td className="td-muted">{datetime(row.regdate)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setDetail(row)}
                        >
                          상세
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              onChange={(p) => update({ page: p }, false)}
            />
          </>
        )
      )}

      {detail && (
        <DetailModal
          title={`유입 기록 #${detail.uid}`}
          onClose={() => setDetail(null)}
          fields={[
            { label: '년월', value: monthLabel(detail.yearmonth) },
            { label: '파트너ID', value: text(detail.partner) },
            { label: '리퍼러 도메인', value: <HostLink host={detail.refhost} /> },
            {
              label: '광고 사이트 등록',
              value: <SiteBadge site={detail.site} />,
            },
            { label: '리퍼러 상세URL', value: text(detail.referer), pre: true },
            { label: '유입파라미터', value: text(detail.param), pre: true },
            { label: '유입IP', value: text(detail.ip) },
            { label: '접속기기', value: <AgentLabel agent={detail.agent} /> },
            { label: '접속시간', value: datetime(detail.regdate) },
          ]}
        />
      )}
    </section>
  );
}
